/**
 * space/fish-swim.js — the deterministic motion behind the fish tank.
 *
 * Every function here is pure: a fish's position is `f(seed, elapsed)` with no
 * integrated state. Two consequences matter.
 *
 * First, the shoal looks identical at 30 fps and at 90 fps, and identical on
 * two machines — which is what "deterministic under seed" has to mean for
 * something that animates every frame.
 *
 * Second, it never draws from the shared seeded generator in `core/testmode`.
 * A fish deciding to turn left mid-round would otherwise shift every subsequent
 * random number in the game and quietly desync another agent's stall test.
 *
 * @module space/fish-swim
 */

import * as THREE from 'three';
import { mulberry32 } from '../core/testmode.js';

/** Smoothstep on [0, 1], clamped. */
function smooth(u) {
  const k = Math.min(1, Math.max(0, u));
  return k * k * (3 - 2 * k);
}

/** Seconds a dart takes to reach full speed. */
const DART_RAMP = 0.55;

/**
 * Swim constants for one fish, derived from its index in the collection rather
 * than from a live random draw, so the same save always yields the same shoal.
 * @param {number} index
 * @returns {object} Opaque seed passed back into the functions below.
 */
export function swimSeed(index) {
  const r = mulberry32((0x9e3779b1 * (index + 1)) >>> 0);
  return {
    speed: 0.26 + r() * 0.2,
    px: r() * Math.PI * 2, py: r() * Math.PI * 2, pz: r() * Math.PI * 2,
    fx: 0.8 + r() * 0.5, fy: 1.5 + r() * 0.9, fz: 1.1 + r() * 0.6,
    ampY: 0.42 + r() * 0.4,
    dartPeriod: 7 + r() * 9,
    dartOffset: r() * 12,
    dartGain: 1.4 + r() * 1.2,
    wiggle: 7 + r() * 4,
  };
}

/**
 * The path parameter at time `t`, including every dart taken so far.
 * Monotonic and closed-form — no accumulation, so no frame-rate drift.
 * @param {object} s @param {number} t @returns {number}
 */
export function pathParam(s, t) {
  const cycles = Math.floor((t + s.dartOffset) / s.dartPeriod);
  return t * s.speed + s.dartGain * (cycles + smooth(((t + s.dartOffset) % s.dartPeriod) / DART_RAMP));
}

/**
 * How hard this fish is darting right now.
 * @param {object} s @param {number} t @returns {number} 0–1.
 */
export function dartAmount(s, t) {
  const within = ((t + s.dartOffset) % s.dartPeriod) / DART_RAMP;
  return within <= 1 ? Math.sin(smooth(within) * Math.PI) : 0;
}

/**
 * Position inside the tank at time `t`.
 * @param {object} s Swim seed.
 * @param {number} t Seconds.
 * @param {THREE.Vector3} centre Tank-local centre of the water volume.
 * @param {THREE.Vector3} bounds Half-extents of the water volume.
 * @param {THREE.Vector3} [out]
 * @returns {THREE.Vector3}
 */
export function swimPosition(s, t, centre, bounds, out = new THREE.Vector3()) {
  const u = pathParam(s, t);
  return out.set(
    centre.x + Math.sin(u * s.fx + s.px) * bounds.x * 0.86,
    centre.y + Math.sin(u * s.fy + s.py) * bounds.y * s.ampY,
    centre.z + Math.cos(u * s.fz + s.pz) * bounds.z * 0.86,
  );
}

/**
 * Expanding rings on the water surface, left behind when a fish darts up
 * through it. Cheap enough that the tank can own a pool of them outright.
 */
export class SplashPool {
  /** @param {THREE.Object3D} parent */
  constructor(parent) {
    this.parent = parent;
    /** @type {Array<{ring: THREE.Mesh, t: number}>} */
    this.live = [];
  }

  /**
   * @param {number} x @param {number} y Surface height. @param {number} z
   */
  spawn(x, y, z) {
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(0.02, 0.05, 14),
      new THREE.MeshBasicMaterial({
        color: 0xcfeaff, transparent: true, opacity: 0.85,
        side: THREE.DoubleSide, depthWrite: false,
      }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.set(x, y, z);
    ring.renderOrder = 4;
    this.parent.add(ring);
    this.live.push({ ring, t: 0 });
  }

  /** @param {number} dt */
  update(dt) {
    for (let i = this.live.length - 1; i >= 0; i--) {
      const s = this.live[i];
      s.t += dt;
      const k = s.t / 0.9;
      if (k >= 1) {
        this.parent.remove(s.ring);
        s.ring.geometry.dispose();
        s.ring.material.dispose();
        this.live.splice(i, 1);
        continue;
      }
      s.ring.scale.setScalar(1 + k * 3.5);
      s.ring.material.opacity = 0.85 * (1 - k);
    }
  }

  clear() {
    for (const s of this.live) {
      this.parent.remove(s.ring);
      s.ring.geometry.dispose();
      s.ring.material.dispose();
    }
    this.live.length = 0;
  }
}
